import { MapUtils } from '../../../utility/map-utils.js';
import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull } from '../../../serialization/enum-utils.js';
import { VersionRange } from '../../core/version-range.js';
import {
  DataModel, DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData,
} from '../../dto/dto.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { Country } from '../country.js';
import { TimeZoneRegion } from '../time-zone-region.js';
import { OrderDomain } from '../order-domain.js';
import { PaymentMethod } from '../payment-method.js';
import { GiftCardCartItemDto } from './gift-card-cart-item.js';
import { CartCustomerDto } from './cart-customer.js';
import { PaymentType } from '../payment-type.js';
import { Language } from '../language.js';
import { OfflineReceiptType } from '../invoicing/offline-receipt-type.js';
import { OfflineCartBase } from './offline-cart-base.js';
import { ElectronicDeliveryMethod } from '../electronic-delivery-method.js';

export class OfflineGiftCardCartDto extends DataModel implements OfflineCartBase {
  static readonly TYPE_ID = '81e8a7459e014c2abf54e7c38d4c9b6a';
  static readonly SCHEMA_VERSION = 1;

  private constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly creatorId: string,
    public readonly items: GiftCardCartItemDto[],
    public readonly customerId: string | null,
    public readonly customer: CartCustomerDto | null,
    public readonly paymentType: PaymentType,
    public readonly paymentMethod: PaymentMethod,
    public readonly language: Language,
    public readonly country: Country,
    public readonly timezone: TimeZoneRegion,
    public readonly domain: OrderDomain,
    public readonly deliveryMethod: ElectronicDeliveryMethod,
    public readonly allowOfflineOrderReceipts: boolean | null,
    public readonly receiptType: OfflineReceiptType | null,
  ) {
    super();
  }

  get $typeId() {
    return OfflineGiftCardCartDto.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    // Version should always be `exactly`.
    // Cart should never be backwards migrated, backend should be updated before frontend.
    return VersionRange.exactly(OfflineGiftCardCartDto.SCHEMA_VERSION);
  }

  static create(args: {
    businessRef: BusinessModuleRef,
    creatorId: string,
    items: GiftCardCartItemDto[],
    customerId: string | null,
    customer: CartCustomerDto | null,
    paymentType: PaymentType,
    paymentMethod: PaymentMethod,
    language: Language,
    country: Country,
    timezone: TimeZoneRegion,
    domain: OrderDomain,
    deliveryMethod: ElectronicDeliveryMethod,
    allowOfflineOrderReceipts: boolean | null,
    receiptType: OfflineReceiptType | null,
  }): OfflineGiftCardCartDto {
    return new OfflineGiftCardCartDto(
        args.businessRef,
        args.creatorId,
        args.items,
        args.customerId,
        args.customer,
        args.paymentType,
        args.paymentMethod,
        args.language,
        args.country,
        args.timezone,
        args.domain,
        args.deliveryMethod,
        args.allowOfflineOrderReceipts,
        args.receiptType,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OfflineGiftCardCartDto {
    return this.create({
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      items: MapUtils.getList<GiftCardCartItemDto>(map, 'items', (e) => {
        return context.fromData(e as SerializedData);
      }),
      creatorId: MapUtils.getString(map, 'creatorId'),
      customerId: MapUtils.getStringOrNull(map, 'customerId'),
      customer: context.fromDataOrNull<CartCustomerDto>(MapUtils.getRecordOrNull(map, 'customer')),
      paymentType: toEnumFromString(MapUtils.getString(map, 'paymentType'), PaymentType),
      paymentMethod: toEnumFromString(MapUtils.getString(map, 'paymentMethod'), PaymentMethod),
      language: toEnumFromString(MapUtils.getString(map, 'language'), Language),
      country: toEnumFromString(MapUtils.getString(map, 'country'), Country),
      timezone: toEnumFromString(MapUtils.getString(map, 'timezone'), TimeZoneRegion),
      domain: toEnumFromString(MapUtils.getString(map, 'domain'), OrderDomain),
      deliveryMethod: toEnumFromString(MapUtils.getString(map, 'deliveryMethod'), ElectronicDeliveryMethod),
      allowOfflineOrderReceipts: MapUtils.getBooleanOrNull(map, 'allowOfflineOrderReceipts'),
      receiptType: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'receiptType'), OfflineReceiptType),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'creatorId': this.creatorId,
      'items': this.items.map((e) => context.toData(e)),
      'customerId': this.customerId,
      'customer': context.toDataOrNull(this.customer),
      'paymentType': fromEnumAsString(this.paymentType),
      'paymentMethod': fromEnumAsString(this.paymentMethod),
      'language': fromEnumAsString(this.language),
      'country': fromEnumAsString(this.country),
      'timezone': fromEnumAsString(this.timezone),
      'domain': fromEnumAsString(this.domain),
      'deliveryMethod': fromEnumAsString(this.deliveryMethod),
      'allowOfflineOrderReceipts': this.allowOfflineOrderReceipts,
      'receiptType': fromEnumAsStringOrNull(this.receiptType),
    };
  }

  clone(): this {return OfflineGiftCardCartDto.create(this) as this;}

  isSame(other: this): boolean {
    return isSameData(this.businessRef, other.businessRef) &&
      this.creatorId === other.creatorId &&
      isSameList(this.items, other.items, isSameData) &&
      this.customerId === other.customerId &&
      isSameData(this.customer, other.customer) &&
      this.paymentType === other.paymentType &&
      this.paymentMethod === other.paymentMethod &&
      this.language === other.language &&
      this.country === other.country &&
      this.timezone === other.timezone &&
      this.domain === other.domain &&
      this.deliveryMethod === other.deliveryMethod &&
      this.allowOfflineOrderReceipts === other.allowOfflineOrderReceipts &&
      this.receiptType === other.receiptType;
  }

  findUniqueTemplateIds(): string[] {
    const templateIds: string[] = [];

    for(const item of this.items) {
      if(!templateIds.includes(item.templateId)) {
        templateIds.push(item.templateId);
      }
    }

    return templateIds;
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof OfflineGiftCardCartDto;
  }

  static assertType(dm: DataModel): dm is OfflineGiftCardCartDto {
    return dm instanceof OfflineGiftCardCartDto;
  }
}

export class OfflineGiftCardCartDtoBuilder extends DataModelBuilder<OfflineGiftCardCartDto> {
  build(context: SerializationContext, map: SerializedData): OfflineGiftCardCartDto {return OfflineGiftCardCartDto.fromMap(context, map);}
}
